import { useEffect, useState, useCallback } from "react";
import {
  Mic,
  Search,
  Trash2,
  Download,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Mail,
  CheckCircle2,
} from "lucide-react";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";

const PAGE_SIZE = 25;

export default function AdminFanQuestions() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async (pageNum, q) => {
    setLoading(true);
    try {
      const result = await api.getFanQuestions({ page: pageNum, pageSize: PAGE_SIZE, q });
      setData(result);
    } catch (err) {
      logger.warn("Fan questions fetch failed:", err.message);
      toast.error("Failed to load fan questions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(page, appliedQuery);
  }, [page, appliedQuery, fetchData]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedQuery(searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedQuery("");
    setPage(1);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete the question from ${item.name} (${item.email})?`)) return;
    try {
      await api.deleteFanQuestion(item.id);
      toast.success("Question removed");
      fetchData(page, appliedQuery);
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await api.exportFanQuestionsCsv();
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = `fan-questions-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const { items, total, total_pages } = data;

  return (
    <div data-testid="admin-fan-questions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
            FAN QUESTIONS
          </h1>
          <p className="font-mono text-white/50 mt-2">
            Submissions from the home page form
            {total ? ` — ${total} total` : ""}
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={exporting || total === 0}
          className="flex items-center gap-2 px-6 py-3 border border-demolition-red text-demolition-red font-teko text-lg uppercase tracking-wider hover:bg-demolition-red hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="export-fan-questions-csv-btn"
        >
          <Download className="w-5 h-5" />
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      {/* Search bar */}
      <form
        onSubmit={handleSearchSubmit}
        className="flex gap-2 mb-6"
        data-testid="fan-questions-search-form"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name, email, location, or question text..."
            className="pl-10 bg-demolition-paper border-white/10 text-white font-mono"
            data-testid="fan-questions-search-input"
          />
        </div>
        <button
          type="submit"
          className="px-6 py-2 bg-demolition-red text-white font-teko text-base uppercase tracking-wider hover:bg-demolition-red-hover transition-colors"
          data-testid="fan-questions-search-btn"
        >
          Search
        </button>
        {appliedQuery && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="px-4 py-2 border border-white/20 text-white/60 font-teko text-base uppercase tracking-wider hover:bg-white/5 hover:text-white transition-colors"
            data-testid="fan-questions-clear-search"
          >
            Clear
          </button>
        )}
      </form>

      {/* List */}
      {loading && (
        <div className="text-center py-12">
          <p className="font-mono text-white/50 animate-pulse">Loading questions...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-demolition-paper border border-white/10 p-12 text-center" data-testid="fan-questions-empty">
          <Mic className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="font-mono text-white/50">
            {appliedQuery
              ? `No questions match "${appliedQuery}".`
              : "No fan questions yet. Submissions from the home page will appear here."}
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-4" data-testid="fan-questions-list">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-demolition-paper border border-white/10 p-6"
              data-testid={`fan-question-${item.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-teko text-xl text-white uppercase tracking-wide">
                    {item.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs font-mono text-white/50">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{item.email}</span>
                    {item.opt_in && (
                      <span className="flex items-center gap-1 text-demolition-red">
                        <CheckCircle2 className="w-3 h-3" />Opted in
                      </span>
                    )}
                    <span>{item.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(item)}
                  className="p-2 text-white/40 hover:text-red-500 hover:bg-white/5 transition-colors flex-shrink-0"
                  data-testid={`delete-fan-question-${item.id}`}
                  aria-label={`Delete question from ${item.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="font-mono text-white/80 text-sm leading-relaxed whitespace-pre-wrap border-l-2 border-demolition-red/50 pl-4">
                {item.question}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && total_pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8" data-testid="fan-questions-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-teko text-base uppercase tracking-wider hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="fan-questions-prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="font-mono text-white/60 text-sm" data-testid="fan-questions-page-indicator">
            Page {page} of {total_pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(total_pages, p + 1))}
            disabled={page >= total_pages}
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-teko text-base uppercase tracking-wider hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="fan-questions-next-page"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
